import React from "react";
import { useTranslation } from "react-i18next";

import Tooltip from "@mui/material/Tooltip";

import { IconButton } from "../shared/FormLayout/IconButtons/IconButton";

import { MeasurementIcon } from "../icons/MeasurementIcon";
import { MouseButton } from "../shared/interfaces/MouseButton";

import { PanelMeasurement, MeasurementDetail, Measurement } from "../shared/interfaces/Result";
import { MeasurementComponent, SubLocation, MeasurementType } from "../shared/enums/Result";
import { CableNaming } from "../shared/enums/UTP2";
import { getSubLocationMeasurements, getTranslatedSubLocationName } from "../shared/helpers/SubLocationHelpers";
import { findPlotPosition } from "./SubLocation/findPlotPosition";
import { ManagedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";

interface Props {
  panelMeasurement: PanelMeasurement;
  componentType: MeasurementComponent;
  sublocation: SubLocation;
  onIconClick: (measurement: MeasurementDetail<Measurement>, position: number | undefined, newTab: boolean) => void;
  cableNaming?: CableNaming;
  assetType?: ManagedSurveyAssetType;
}

export function ComponentMeasurementIcons(props: Props) {
  const { t } = useTranslation();

  function buildTooltipTranslationKey(
    measurementType: string,
    componentType: MeasurementComponent,
    sublocation: SubLocation,
  ) {
    const locationKey =
      sublocation === SubLocation.None
        ? `$t(MeasurementComponents.${componentType} )`
        : getTranslatedSubLocationName(sublocation, componentType, props.cableNaming);

    if (measurementType === "") return t("results.noMeasurementFor", { location: locationKey });
    else
      return t("results.measurementFor", {
        measurementType: `$t(MeasurementTypes.${measurementType} )`,
        location: locationKey,
      });
  }

  function buildIconList(
    measurementsList: Measurement[],
    measurementType: MeasurementType,
    componentType: MeasurementComponent,
    sublocation: SubLocation,
    panel: PanelMeasurement,
  ) {
    return measurementsList.map((measurement: Measurement) => {
      const position = findPlotPosition(
        measurement.MeasurementType,
        props.panelMeasurement,
        props.componentType,
        sublocation,
        props.assetType,
      );

      const measurementDetail: MeasurementDetail<Measurement> = {
        measurement: measurement,
        measurementType: measurementType,
        panelName: panel.PanelName,
        panelNo: panel.PanelNo,
        panel360AssetId: panel.Panel360AssetId,
      };

      const handleMiddleClick = (event: React.MouseEvent) => {
        if (event.button === MouseButton.Auxiliary) {
          event.preventDefault();
          props.onIconClick(measurementDetail, position, true);
        }
      };

      return (
        <IconButton
          key={`${measurementType}-${componentType}-${sublocation}`}
          tooltip={buildTooltipTranslationKey(measurementType, componentType, sublocation)}
          icon={<MeasurementIcon measurementType={measurementType} ragStatus={measurement.RAGStatus} />}
          onClick={(event) => props.onIconClick(measurementDetail, position, event.ctrlKey)}
          // onAuxClick does not fire reliably when the page is scrollable
          onMouseDown={handleMiddleClick}
          sx={{ padding: 0 }}
        />
      );
    });
  }

  function getMeasurementIconsByComponentAndSublocation(
    panelMeasurement: PanelMeasurement,
    componentType: MeasurementComponent,
    sublocation: SubLocation,
  ) {
    const cablePdMeasurements = getSubLocationMeasurements(
      panelMeasurement.CablePDMeasurements,
      componentType,
      sublocation,
    );
    const cablePdIcons = buildIconList(
      cablePdMeasurements,
      MeasurementType.CablePD,
      componentType,
      sublocation,
      panelMeasurement,
    );

    const tevMeasurements = getSubLocationMeasurements(panelMeasurement.TEVMeasurements, componentType, sublocation);
    const tevIcons = buildIconList(tevMeasurements, MeasurementType.TEV, componentType, sublocation, panelMeasurement);

    const uhfMeasurements = getSubLocationMeasurements(panelMeasurement.UHFMeasurements, componentType, sublocation);
    const uhfPdIcons = buildIconList(
      uhfMeasurements,
      MeasurementType.UHF,
      componentType,
      sublocation,
      panelMeasurement,
    );

    const ultrasonicMeasurements = getSubLocationMeasurements(
      panelMeasurement.UltraSonicMeasurements,
      componentType,
      sublocation,
    );
    const ultrasonicIcons = buildIconList(
      ultrasonicMeasurements,
      MeasurementType.Ultra,
      componentType,
      sublocation,
      panelMeasurement,
    );

    const vpisMeasurements = getSubLocationMeasurements(panelMeasurement.VPISMeasurements, componentType, sublocation);
    const vpisIcons = buildIconList(
      vpisMeasurements,
      MeasurementType.VPIS,
      componentType,
      sublocation,
      panelMeasurement,
    );

    const icons = [...cablePdIcons, ...tevIcons, ...uhfPdIcons, ...ultrasonicIcons, ...vpisIcons];

    return icons.length > 0 ? icons : undefined;
  }

  return (
    <React.Fragment>
      {getMeasurementIconsByComponentAndSublocation(props.panelMeasurement, props.componentType, props.sublocation) || (
        <Tooltip title={buildTooltipTranslationKey("", props.componentType, props.sublocation)}>
          <div className="w-100">&nbsp;</div>
        </Tooltip>
      )}
    </React.Fragment>
  );
}
